import React, { useState, useEffect } from 'react';
import { Crown, ArrowLeft } from 'lucide-react';
import { Account, ProductWithPrices, StripePrice, CreateCheckoutSessionRequest } from '../types';
import { api } from '../services/api';

interface DynamicCheckoutPageProps {
  onBack: () => void;
  account: Account;
}

export const DynamicCheckoutPage: React.FC<DynamicCheckoutPageProps> = ({ onBack, account }) => {
  const [productWithPrices, setProductWithPrices] = useState<ProductWithPrices | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPriceId, setSelectedPriceId] = useState<string | null>(null);
  const [isProcessingCheckout, setIsProcessingCheckout] = useState(false);

  // Load the active product for this account's connected account
  useEffect(() => {
    const loadActiveProduct = async () => {
      if (!account.stripeConnectedAccountId) {
        setError('No Stripe connected account found for this account');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null); // Clear any previous errors
        const activeProduct = await api.getActiveProduct(account.stripeConnectedAccountId);
        setProductWithPrices(activeProduct);
        
        // Auto-select the first price if there's only one, or no selection yet
        if (activeProduct.prices.length === 1) {
          setSelectedPriceId(activeProduct.prices[0].id);
        }
      } catch (error) {
        console.error('Failed to load active product:', error);
        setError(error instanceof Error ? error.message : 'Failed to load product information');
      } finally {
        setIsLoading(false);
      }
    };

    loadActiveProduct();
  }, [account?.stripeConnectedAccountId]); // Use optional chaining to handle null account

  const handlePriceSelect = (priceId: string) => {
    setSelectedPriceId(priceId);
  };

  const handleCheckout = async () => {
    if (!selectedPriceId) {
      setError('Please select a pricing option');
      return;
    }

    setIsProcessingCheckout(true);
    setError(null);

    try {
      const baseUrl = window.location.origin;
      const successUrl = `${baseUrl}/success?plan=${encodeURIComponent(productWithPrices?.product.name || '')}`;
      const cancelUrl = `${baseUrl}/cancel`;

      const request: CreateCheckoutSessionRequest = {
        accountId: account.id,
        priceId: selectedPriceId,
        successUrl,
        cancelUrl,
        allowPromoCodes: true
      };

      const response = await api.createCheckoutSession(request);
      
      // Redirect to Stripe Checkout
      window.location.href = response.url;
    } catch (error) {
      console.error('Checkout error:', error);
      setError(error instanceof Error ? error.message : 'An error occurred during checkout');
    } finally {
      setIsProcessingCheckout(false);
    }
  };

  const formatPrice = (price: StripePrice) => {
    const amount = ((price.unit_amount || 0) / 100).toFixed(2);
    
    if (price.recurring && price.recurring.interval === 'month') {
      return `$${amount}/month`;
    }
    
    if (price.recurring && price.recurring.interval === 'year') {
      return `$${amount}/year`;
    }
    
    return `$${amount}`;
  };

  const getIntervalDisplayName = (price: StripePrice) => {
    if (!price.recurring) return 'One-time payment';
    
    const { interval } = price.recurring;
    if (interval === 'month') {
      return 'Monthly billing';
    }
    
    if (interval === 'year') {
      return 'Annual billing';
    }
    
    return 'Subscription';
  };

  if (isLoading) {
    return (
      <div className="dynamic-checkout-page py-8 flex items-center justify-center">
        <div className="text-center">
          <span className="loading loading-spinner loading-lg"></span>
          <p className="mt-4 text-base-content">Loading pricing information...</p>
        </div>
      </div>
    );
  }

  if (error || !productWithPrices) {
    return (
      <div className="dynamic-checkout-page py-8 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="alert alert-error">
            <span>{error || 'Product information not available'}</span>
          </div>
          <button onClick={onBack} className="btn btn-primary mt-4">
            <ArrowLeft size={20} />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dynamic-checkout-page bg-base-100">
      <div className="container mx-auto px-4 py-6 max-w-4xl mt-8">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className="btn btn-ghost btn-sm gap-2"
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-xl font-semibold text-base-content mb-2">Choose Your Plan</h1>
          <p className="text-sm text-base-content/70">
            Select the billing option that works best for you
          </p>
        </div>

        {/* Product Information */}

        {/* Error Message */}
        {error && (
          <div className="alert alert-error mb-8">
            <span>{error}</span>
          </div>
        )}

        {/* Price Options - Simplified */}
        <div className="flex justify-center mb-8">
          <div className="w-full max-w-md space-y-3">
            {productWithPrices.prices.map((price) => {
              const isSelected = selectedPriceId === price.id;
              
              return (
                <div
                  key={price.id}
                  className={`card bg-base-100 border cursor-pointer transition-all duration-200 ${
                    isSelected 
                      ? 'border-primary ring-2 ring-primary shadow-lg' 
                      : 'border-base-300 hover:border-base-400 hover:shadow-md'
                  }`}
                  onClick={() => handlePriceSelect(price.id)}
                >
                  <div className="card-body p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Radio Button */}
                        <input
                          type="radio"
                          className="radio radio-primary radio-sm"
                          checked={isSelected}
                          onChange={() => handlePriceSelect(price.id)}
                          aria-label={`Select ${getIntervalDisplayName(price)}`}
                        />
                        
                        {/* Price Info */}
                        <div>
                          <div className="text-base font-medium text-base-content">
                            {getIntervalDisplayName(price)}
                          </div>
                          {price.nickname && (
                            <div className="text-sm text-base-content/60">
                              {price.nickname}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Price Amount */}
                      <div className="text-right">
                        <div className="text-xl font-bold text-primary">
                          ${((price.unit_amount || 0) / 100).toFixed(0)}
                        </div>
                        <div className="text-xs text-base-content/60">
                          {price.recurring?.interval === 'month' ? '/month' : price.recurring?.interval === 'year' ? '/year' : ''}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Selected Price Summary */}
        {selectedPriceId && (
          <div className="bg-base-200 rounded-lg p-4 mb-6 max-w-md mx-auto">
            <h3 className="text-base font-semibold text-base-content mb-3">Order Summary</h3>
            {(() => {
              const selectedPrice = productWithPrices.prices.find((p: StripePrice) => p.id === selectedPriceId);
              if (!selectedPrice) return null;
              
              return (
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-base font-medium text-base-content">{productWithPrices.product.name}</div>
                    <div className="text-sm text-base-content/70">
                      {formatPrice(selectedPrice)}
                    </div>
                  </div>
                  <div className="text-xl font-bold text-success">
                    ${((selectedPrice.unit_amount || 0) / 100).toFixed(2)}
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Checkout Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={handleCheckout}
            disabled={!selectedPriceId || isProcessingCheckout}
            className={`btn btn-success px-8 max-w-md w-full ${
              !selectedPriceId ? 'btn-disabled' : ''
            }`}
          >
            {isProcessingCheckout ? (
              <>
                <span className="loading loading-spinner loading-sm"></span>
                Processing...
              </>
            ) : (
              <>
                <Crown size={18} />
                {selectedPriceId ? 'Continue to Secure Checkout' : 'Select a Plan to Continue'}
              </>
            )}
          </button>
        </div>

        {/* Security Badge */}
        <div className="text-center">
          <div className="text-sm text-base-content/60">
            🔒 Secure checkout powered by Stripe
          </div>
          <div className="text-xs text-base-content/60 mt-2">
            Secure payment powered by Stripe • Cancel anytime
          </div>
        </div>
      </div>
    </div>
  );
};
